import { create } from "zustand";
import { persist, type PersistStorage, type StorageValue } from "zustand/middleware";

import type { ComposeLevel } from "../../../component-library/compose-sheet/ComposeSheet.js";
import { getBoard, getChildBoards, getParentBoard } from "@/entities/board/index.js";
import type { Post } from "@/entities/post/index.js";
import { STORAGE_KEYS } from "@/shared/storage/keys.js";
import { parentIdForBoardId, targetSummaryForBoardIds } from "./boardPickerOptions.js";
import type { ComposeDraftImage } from "./composeImages.js";

export type ComposeDraft = {
  primaryBoardId: string;
  boardIds: string[];
  targetSummary: string;
  topic: string;
  text: string;
  images: ComposeDraftImage[];
  level: ComposeLevel;
  identityMode: Post["identityMode"];
  font: string;
  emotion: string;
};

type ComposeState = {
  isOpen: boolean;
  draft: ComposeDraft;
  editingPostId: string | null;
  editingAuthorId: string | null;
  coPostOpen: boolean;
  boardPickerParentId: string;
  boardPickerQuery: string;
  message: string;
  openCompose: (options?: { primaryBoardId?: string }) => void;
  openComposeForEdit: (post: Post) => void;
  closeCompose: () => void;
  updateDraft: (patch: Partial<ComposeDraft>) => void;
  setCoPostOpen: (isOpen: boolean) => void;
  setBoardPickerParentId: (parentId: string) => void;
  setBoardPickerQuery: (query: string) => void;
  setMessage: (message: string) => void;
  resetDraft: () => void;
};

const initialDraft: ComposeDraft = {
  primaryBoardId: "life",
  boardIds: ["life"],
  targetSummary: "生活碎片",
  topic: "",
  text: "",
  images: [],
  level: 2,
  identityMode: "anonymous",
  font: "classic",
  emotion: "",
};

/**
 * Persisted shape — only the resumable subset of the draft. Excludes:
 *   - images: blob: URLs from `URL.createObjectURL` don't survive reload,
 *             and re-uploading rehydrated File objects requires a server
 *             round-trip we don't want to do silently. User re-attaches.
 *   - targetSummary: derivable from boardIds + primaryBoardId at restore.
 *   - editing*: edit sessions shouldn't resume on reload (avoid silently
 *               clobbering an in-flight edit if the user reopens the tab
 *               with a half-typed draft from a different post).
 *   - isOpen / coPostOpen / boardPickerParentId / boardPickerQuery /
 *     message: ephemeral UI state, not draft content.
 *
 * Mirrors logic-animation-audit-2026-05-19.md §L3 partialize spec.
 */
type PersistedComposeState = {
  draft: Pick<
    ComposeDraft,
    | "primaryBoardId"
    | "boardIds"
    | "topic"
    | "text"
    | "level"
    | "identityMode"
    | "font"
    | "emotion"
  >;
};

/**
 * Late-binding localStorage adapter — resolves `window.localStorage` on
 * every call instead of pinning it at construction time. Required because
 * vitest's setup overrides `window.localStorage` AFTER modules are imported,
 * so the default `createJSONStorage(() => localStorage)` ends up holding a
 * stale jsdom reference. Resolving fresh per-call also keeps SSR safe.
 */
// partialize may return undefined (skip persistence while editing), so the
// adapter type widens to `PersistedComposeState | undefined`.
type ComposeDraftPersistShape = PersistedComposeState | undefined;
const composeDraftStorage: PersistStorage<ComposeDraftPersistShape> = {
  getItem(name) {
    if (typeof window === "undefined") return null;
    const raw = window.localStorage.getItem(name);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as StorageValue<ComposeDraftPersistShape>;
    } catch {
      return null;
    }
  },
  setItem(name, value) {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(name, JSON.stringify(value));
  },
  removeItem(name) {
    if (typeof window === "undefined") return;
    window.localStorage.removeItem(name);
  },
};

/**
 * P0-3 (audit §L3 / RULE 17 AP5): replaces H5 `scheduleAutoSaveDraft`
 * (main.js:6560) — handwritten setTimeout(2000) + apiFetch POST chain —
 * with Zustand's `persist` middleware writing to localStorage. Auto
 * debounce (zustand persists synchronously on every set() but JSON.stringify
 * is fast enough for our payload), JSON serialization, and storage hydration
 * are all handled by the library; zero handwritten timers / event handlers.
 *
 * Beyond H5: persists across browser sessions (H5 only persisted to
 * /api/drafts which required login + network). Survives offline scenarios.
 */
export const useComposeStore = create<ComposeState>()(
  persist(
    (set) => ({
  isOpen: false,
  draft: initialDraft,
  editingPostId: null,
  editingAuthorId: null,
  coPostOpen: false,
  boardPickerParentId: "daily",
  boardPickerQuery: "",
  message: "",

  openCompose(options) {
    // audit-2026-05-18 (UX-5.1): mirror H5 startComposeFlow (main.js:6384)
    // + makeParentPublishTarget (main.js:5985). When the seed id is a *parent*
    // (e.g. user picked "日常生活" in PublishTargetSheet), expand to all child
    // boards under that parent — first child becomes primary, the rest become
    // same-parent co-post candidates. Otherwise treat it as a child board id
    // directly. Without options, fall back to the default "life" board.
    const seedId = options?.primaryBoardId;
    let primaryBoardId = "";
    let boardIds: string[] = [];
    if (seedId) {
      if (getParentBoard(seedId)) {
        const children = getChildBoards(seedId);
        primaryBoardId = children[0]?.id ?? "";
        boardIds = children.map((board) => board.id);
      } else if (getBoard(seedId)) {
        primaryBoardId = seedId;
        boardIds = [seedId];
      }
    }
    const draft = primaryBoardId
      ? {
          ...initialDraft,
          primaryBoardId,
          boardIds,
          targetSummary: targetSummaryForBoardIds(boardIds, primaryBoardId),
        }
      : initialDraft;
    set({
      isOpen: true,
      draft,
      editingPostId: null,
      editingAuthorId: null,
      coPostOpen: false,
      boardPickerParentId: primaryBoardId
        ? parentIdForBoardId(primaryBoardId)
        : "daily",
      message: "",
    });
  },

  openComposeForEdit(post) {
    const boardIds = post.boardIds.length ? post.boardIds : [post.boardId];
    set({
      isOpen: true,
      editingPostId: post.id,
      editingAuthorId: post.authorId,
      coPostOpen: false,
      boardPickerParentId: parentIdForBoardId(post.boardId),
      boardPickerQuery: "",
      message: "",
      draft: {
        primaryBoardId: post.boardId,
        boardIds,
        targetSummary: targetSummaryForBoardIds(boardIds, post.boardId),
        topic: post.topic ?? "",
        text: post.text,
        images: (post.images ?? []).map((image, index) => ({
          id: `${post.id}-image-${index}`,
          src: image.src,
          alt: image.alt,
        })),
        level: post.level,
        identityMode: post.identityMode,
        font: post.font ?? initialDraft.font,
        emotion: post.emotion ?? "",
      },
    });
  },

  closeCompose() {
    set({
      isOpen: false,
      editingPostId: null,
      editingAuthorId: null,
      coPostOpen: false,
      message: "",
    });
  },

  updateDraft(patch) {
    set((state) => ({ draft: { ...state.draft, ...patch }, message: "" }));
  },

  setCoPostOpen(isOpen) {
    set({ coPostOpen: isOpen, message: "" });
  },

  setBoardPickerParentId(parentId) {
    set({ boardPickerParentId: parentId, boardPickerQuery: "", message: "" });
  },

  setBoardPickerQuery(query) {
    set({ boardPickerQuery: query, message: "" });
  },

  setMessage(message) {
    set({ message });
  },

  resetDraft() {
    set({
      draft: initialDraft,
      editingPostId: null,
      editingAuthorId: null,
      coPostOpen: false,
      boardPickerParentId: "daily",
      boardPickerQuery: "",
      message: "",
    });
  },
}),
    {
      name: STORAGE_KEYS.composeDraft,
      version: 1,
      storage: composeDraftStorage,
      // Only persist the resumable draft fields. See PersistedComposeState
      // for rationale. When `editingPostId !== null` the user is editing an
      // existing post; we don't want that interim text to overwrite the
      // localStorage new-post draft, so we skip persistence in that case
      // by returning undefined (zustand keeps the previous storage value).
      partialize: (state): PersistedComposeState | undefined => {
        if (state.editingPostId !== null) return undefined;
        return {
          draft: {
            primaryBoardId: state.draft.primaryBoardId,
            boardIds: state.draft.boardIds,
            topic: state.draft.topic,
            text: state.draft.text,
            level: state.draft.level,
            identityMode: state.draft.identityMode,
            font: state.draft.font,
            emotion: state.draft.emotion,
          },
        };
      },
      // On rehydrate, merge the persisted subset back into the live draft.
      // `targetSummary` is recomputed from boardIds + primaryBoardId so it
      // never goes stale across schema bumps.
      merge: (persistedState, currentState) => {
        const persisted = persistedState as Partial<PersistedComposeState> | undefined;
        const persistedDraft = persisted?.draft;
        if (!persistedDraft) return currentState;
        const boardIds = persistedDraft.boardIds?.length
          ? persistedDraft.boardIds
          : [persistedDraft.primaryBoardId || initialDraft.primaryBoardId];
        const primaryBoardId =
          persistedDraft.primaryBoardId || initialDraft.primaryBoardId;
        return {
          ...currentState,
          draft: {
            ...initialDraft,
            ...persistedDraft,
            primaryBoardId,
            boardIds,
            targetSummary: targetSummaryForBoardIds(boardIds, primaryBoardId),
            // images intentionally reset — blob URLs are dead post-reload.
            images: [],
          },
        };
      },
    },
  ),
);
